import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { ShiftRemark } from "../models/ShiftRemark";
import {
	StackerBundle,
	StackerBundleDraft,
	StackerHeat,
} from "../models/StackerBundle";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import {
	BundleDetail,
	BundleValues,
	FinishedGroup,
	FinishedHeatOption,
	ScrapEntry,
	ScrapValues,
	StackerShiftSource,
} from "../models/FinishedBundle";

const moment = getMomentTz();

export interface StackerReport {
	bundles: StackerBundle[];
	remark: ShiftRemark | null;
	heat: StackerHeat | null;
	details: BundleDetail[];
	shiftSource: StackerShiftSource | null;
	heatOptions: FinishedHeatOption[];
	scrap: ScrapEntry[];
}

const toDate = (value: unknown) =>
	value ? moment.utc(value as string).toDate() : null;

export const getStackerReport = async (
	params: { shiftId: number; defaultAreaId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ShiftId", value: params.shiftId },
	];

	const resp = await apiService.callV2(
		params.defaultAreaId == MILL_1.id
			? "[M1].[GetStackerReportByShift]"
			: "[M2].[GetStackerReportByShift]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching stacker report");
	}

	let bundles = get(resp, "data.tables[0].rows", []) as StackerBundle[];
	bundles = bundles.map((b) => ({
		...b,
		CreatedAt: b.CreatedAt ? moment.utc(b.CreatedAt).toDate() : null,
		ModifiedAt: b.ModifiedAt ? moment.utc(b.ModifiedAt).toDate() : null,
	}));

	const remarkRow = get(
		resp,
		"data.tables[1].rows[0]",
		null,
	) as ShiftRemark | null;

	const remark = remarkRow
		? {
				...remarkRow,
				CreatedAt: remarkRow.CreatedAt
					? moment.utc(remarkRow.CreatedAt).toDate()
					: null,
				ModifiedAt: remarkRow.ModifiedAt
					? moment.utc(remarkRow.ModifiedAt).toDate()
					: null,
			}
		: null;

	const heat = get(resp, "data.tables[2].rows[0]", null) as StackerHeat | null;

	const details = (get(resp, "data.tables[3].rows", []) as BundleDetail[]).map(
		(d) => ({
			...d,
			Timestamp: toDate(d.Timestamp) as Date,
			PlcTimestamp: toDate(d.PlcTimestamp),
			CreatedAt: toDate(d.CreatedAt),
			UpdatedAt: toDate(d.UpdatedAt),
			CheckedAt: toDate(d.CheckedAt),
		}),
	);
	const shiftSource = get(
		resp,
		"data.tables[4].rows[0]",
		null,
	) as StackerShiftSource | null;
	const heatOptions = (
		get(resp, "data.tables[5].rows", []) as FinishedHeatOption[]
	).map((h) => ({ ...h, Start: toDate(h.Start) as Date }));
	const scrap = (get(resp, "data.tables[6].rows", []) as ScrapEntry[]).map(
		(sc) => ({
			...sc,
			CreatedAt: toDate(sc.CreatedAt),
			UpdatedAt: toDate(sc.UpdatedAt),
		}),
	);

	bundles = bundles.map((b) => {
		const g = b as FinishedGroup;
		return g.Source === "P"
			? {
					...g,
					FirstBundleTime: toDate(g.FirstBundleTime),
					LastBundleTime: toDate(g.LastBundleTime),
				}
			: b;
	});

	return { bundles, remark, heat, details, shiftSource, heatOptions, scrap };
};

export const upsertStackerBundle = async (params: {
	draft: StackerBundleDraft;
	defaultAreaId: number;
}) => {
	const apiService = new MESApiService();
	const { draft } = params;

	const parameters: spParameter[] = [
		{ name: "StackerBundleId", value: draft.StackerBundleId },
		{ name: "ShiftId", value: draft.ShiftId },
		{ name: "SectionId", value: draft.SectionId },
		{ name: "HeatId", value: draft.HeatId },
		{ name: "LengthInches", value: draft.LengthInches },
		{ name: "GoodPieces", value: draft.GoodPieces ?? 0 },
		{ name: "TotalWeight", value: draft.TotalWeight },
		{ name: "ScrapPieces", value: draft.ScrapPieces ?? null },
		{ name: "ScrapReason", value: draft.ScrapReason },
	];

	const resp = await apiService.callV2(
		"[M1].[UpsertStackerBundle]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving bundle");
	}
};

export const deleteStackerBundle = async (params: {
	stackerBundleId: number;
	defaultAreaId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "StackerBundleId", value: params.stackerBundleId },
	];

	const resp = await apiService.callV2(
		"[M1].[DeleteStackerBundle]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting bundle");
	}
};

// Mill 2: edit bundles in place (bundleIds) or create manual bundles (no bundleIds) - M2.UpsertBundles.
// On an edit only the values passed change.
export const upsertBundles = async (
	params: BundleValues & {
		bundleIds?: number[];
		shiftId?: number;
		quantity?: number;
	},
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "BundleIds", value: params.bundleIds?.join(",") || null },
		{ name: "ShiftId", value: params.shiftId ?? null },
		{ name: "HeatId", value: params.heatId ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "LengthFt", value: params.lengthFt ?? null },
		{ name: "Pieces", value: params.pieces ?? null },
		{ name: "Comment", value: params.comment ?? null },
		{ name: "Quantity", value: params.quantity ?? 1 },
		{ name: "Timestamp", value: params.timestamp?.toISOString() ?? null },
	];

	const resp = await apiService.callV2("[M2].[UpsertBundles]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving bundles");
	}
};

export const setBundlesActive = async (params: {
	bundleIds: number[];
	isActive: boolean;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "BundleIds", value: params.bundleIds.join(",") },
		{ name: "IsActive", value: params.isActive },
	];

	const resp = await apiService.callV2("[M2].[UpsertBundles]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error updating bundles");
	}
};

// Mill 2: a person checked the flagged bundles (heat to check / mill stopped) were right - M2.UpsertBundles @Checked
export const checkBundles = async (params: { bundleIds: number[] }) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "BundleIds", value: params.bundleIds.join(",") },
		{ name: "Checked", value: true },
	];

	const resp = await apiService.callV2("[M2].[UpsertBundles]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error checking bundles");
	}
};

export const upsertScrapPieces = async (
	params: ScrapValues & { scrapPiecesId?: number; shiftId?: number },
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ScrapPiecesIds", value: params.scrapPiecesId?.toString() ?? null },
		{ name: "ShiftId", value: params.shiftId ?? null },
		{ name: "HeatId", value: params.heatId ?? null },
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "LengthFt", value: params.lengthFt ?? null },
		{ name: "Pieces", value: params.pieces ?? null },
		{ name: "Reason", value: params.reason ?? null },
	];

	const resp = await apiService.callV2("[M2].[UpsertScrapPieces]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving scrap");
	}
};

export const setScrapPiecesActive = async (params: {
	scrapPiecesIds: number[];
	isActive: boolean;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ScrapPiecesIds", value: params.scrapPiecesIds.join(",") },
		{ name: "IsActive", value: params.isActive },
	];

	const resp = await apiService.callV2("[M2].[UpsertScrapPieces]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error removing scrap");
	}
};

export const upsertStackerShiftRemark = async (params: {
	shiftId: number;
	comment: string | null;
	defaultAreaId: number;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ShiftId", value: params.shiftId },
		{ name: "Comment", value: params.comment },
	];

	const resp = await apiService.callV2(
		params.defaultAreaId == MILL_1.id
			? "[M1].[UpsertStackerShiftRemark]"
			: "[M2].[UpsertStackerShiftRemark]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error saving remark");
	}
};
